import { Navigate, Route, Routes, useNavigate, useLocation } from "react-router-dom";
import { useCallback } from "react";
import { ThemeProvider, useColorScheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { Badge, Box, Typography } from "@mui/material";
import { createBrandTheme, Logo, colors } from "brand";
import {
  AccountMenu,
  AppBar,
  AppFooter,
  AppLayout,
  CognitoAuthProvider,
  ProtectedRoute,
  CallbackPage,
  LogoutPage,
  LoggedOutPage,
  ActiveOrgProvider,
  ActiveShowProvider,
  OrgSwitcher,
  ShowSwitcher,
  useActiveOrg,
  useActiveShow,
  UserDisplayName,
  NavRail,
  BuildBadge,
  UsaBadge,
} from "common-fe";
import { OrganizationApi, Configuration } from "account-common";
import { useScoreApi, unwrap } from "./api/scoreApi";
import { useAuth } from "react-oidc-context";
import { useTranslation } from "i18n-fe";
import { useClassesNeedingPlacings } from "./hooks/useClassesNeedingPlacings";
import { useReviewQueue } from "./hooks/useReviewQueue";
import { useCatalogDefs } from "./hooks/useCatalogDefs";
import DashboardIcon from "@mui/icons-material/Dashboard";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import DescriptionIcon from "@mui/icons-material/Description";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import GavelIcon from "@mui/icons-material/Gavel";
import PublishIcon from "@mui/icons-material/Publish";
import LogoutIcon from "@mui/icons-material/Logout";
import SettingsIcon from "@mui/icons-material/Settings";
import DnsIcon from "@mui/icons-material/Dns";
import HandshakeIcon from "@mui/icons-material/Handshake";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import ClassesIcon from "./components/icons/ClassesIcon";
import HorseIcon from "./components/icons/HorseIcon";
import RiderIcon from "./components/icons/RiderIcon";
import RideIcon from "./components/icons/RideIcon";
import ShowDashboard from "./pages/ShowDashboard";
import ClassManager from "./pages/ClassManager";
import HorseManager from "./pages/HorseManager";
import PeopleManager from "./pages/PeopleManager";
import JudgeManager from "./pages/JudgeManager";
import ReportsPage from "./pages/ReportsPage";
import PublishResults from "./pages/PublishResults";
import RideEditor from "./pages/RideEditor";
import RidePointer from "./pages/RidePointer";
import ClassPlacings from "./pages/ClassPlacings";
import ReviewQueue from "./pages/ReviewQueue";
import TestDefEditor from "./pages/TestDefEditor";
import SettingsPage from "./pages/Settings";
import Sponsors from "./pages/Sponsors";
import FoxboxInstances from "./pages/FoxboxInstances";
import ScoreEntitlementGate from "./components/ScoreEntitlementGate";
import { handleLogout } from "./util/logout";

const ACCOUNT_API_BASE_URL = import.meta.env.VITE_ACCOUNT_API_BASE_URL;

// Inject the org-list fetcher (built from the generated account-common client)
// into the shared ActiveOrgProvider, which never calls fetch itself — the org
// list is served by the ACCOUNT API. Same pattern as manager-fe. The selected
// org rides on the X-Org-Id header (see common-fe useApiConfig).
const ScoreadminActiveOrgProvider = ({ children }) => {
  const auth = useAuth();
  const accessToken = auth.user?.access_token;
  const listOrgs = useCallback(
    async (isSuper) => {
      if (!accessToken) return [];
      const api = new OrganizationApi(
        new Configuration({ basePath: ACCOUNT_API_BASE_URL, accessToken })
      );
      const res = isSuper
        ? await api.listAllOrganizations(true) // enabledOnly (#2074): switcher shows only superadminEnabled orgs
        : await api.listOrganizations();
      return res.data?.status === "success" && Array.isArray(res.data.data)
        ? res.data.data
        : [];
    },
    [accessToken]
  );
  return <ActiveOrgProvider listOrgs={listOrgs}>{children}</ActiveOrgProvider>;
};

// #521 — shared active-show, org-scoped. listShows is the generated score
// client (already X-Org-Id scoped); reloads when the active org changes.
const ScoreadminActiveShowProvider = ({ children }) => {
  const scoreApi = useScoreApi();
  const { activeOrgId } = useActiveOrg();
  const listShows = useCallback(
    async () => unwrap(await scoreApi.shows.listShows()),
    [scoreApi]
  );
  return (
    <ActiveShowProvider listShows={listShows} orgId={activeOrgId}>
      {children}
    </ActiveShowProvider>
  );
};

function BrandLogoMark() {
  const navigate = useNavigate();
  const { t } = useTranslation("common");
  return (
    <Box
      onClick={() => navigate("/")}
      sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
    >
      <Logo
        style={{
          paddingLeft: 8,
          height: 40,
          width: "auto",
          marginRight: 16,
          flexShrink: 0,
        }}
      />
      <Typography variant="h2" sx={{ fontSize: "1.1rem" }}>
        {t("app.title")}
      </Typography>
    </Box>
  );
}

function AdminShell({ children }) {
  const navigate = useNavigate();
  const { t } = useTranslation("scoreadmin");
  const { mode, setMode } = useColorScheme();
  const { activeShowId } = useActiveShow();
  // #2373 — the Superuser/Foxbox nav item is gated on isSuper alone (same
  // signal ScoreEntitlementGate and OrgSwitcher already use), independent of
  // activeShowId above: instance-level, not show-scoped, so it must not
  // appear/disappear as the active show changes.
  const { isSuper } = useActiveOrg();
  // #862 — same derivation the Classes-page "needs placings" chip uses (#340),
  // so the nav badge and the chip can never disagree (AC4).
  const { needsPlacingsCount } = useClassesNeedingPlacings(activeShowId);
  // #862 — org-wide count of manual rides still awaiting review (#387).
  const { rides: reviewQueueRides } = useReviewQueue();
  const reviewQueueCount = reviewQueueRides.length;
  // card #1662 — app-init hydration of the global test catalog (GET
  // /catalog) into IndexedDB; return value unused here, TestDefEditor reads
  // the same cache directly. AdminShell mounts once per authenticated
  // session regardless of which page loads first, so this is the "app-init"
  // trigger point docs/score/catalog-audit-findings.md section 9a asks for.
  useCatalogDefs();

  const handleThemeToggle = () => {
    setMode(mode === "dark" ? "light" : "dark");
  };

  const navItems = [
    { icon: <DashboardIcon />, text: t("nav.dashboard"), onClick: () => navigate("/") },
    { icon: <ClassesIcon />, text: t("nav.classes"), onClick: () => navigate("/classes") },
    { icon: <HorseIcon />, text: t("nav.horses"), onClick: () => navigate("/horses") },
    { icon: <RiderIcon />, text: t("nav.people"), onClick: () => navigate("/people") },
    { icon: <GavelIcon />, text: t("nav.judges"), onClick: () => navigate("/judges") },
    { icon: <RideIcon />, text: t("nav.rides"), onClick: () => navigate("/rides") },
    {
      icon: <SwapHorizIcon />,
      text: t("nav.ridePointer"),
      onClick: () => navigate("/ride-pointer"),
    },
    {
      // #862 — badgeContent=0 still renders a literal "0" text node (hidden
      // via the MuiBadge-invisible CSS class, not removed from the DOM):
      // MUI's Badge caches the *previous* badgeContent in a ref so it can
      // fade the old number out, and on mount/while loading the count is 0
      // across several re-renders, so that cached "previous" value is also
      // 0. Passing `undefined` instead of a literal 0 sidesteps that cache
      // path entirely (Badge's own `badgeContent == null` check forces
      // invisible=true with no text), so the badge is truly absent — never
      // a literal "0" — at zero (AC3), with no extra conditional needed.
      icon: (
        <Badge
          badgeContent={needsPlacingsCount || undefined}
          color="warning"
          max={99}
          data-testid="nav-placings-badge"
        >
          <EmojiEventsIcon />
        </Badge>
      ),
      text: t("nav.placings"),
      onClick: () => navigate("/placings"),
    },
    {
      icon: (
        <Badge
          badgeContent={reviewQueueCount || undefined}
          color="warning"
          max={99}
          data-testid="nav-review-queue-badge"
        >
          <FactCheckIcon />
        </Badge>
      ),
      text: t("nav.reviewQueue"),
      onClick: () => navigate("/review-queue"),
    },
    // #2199: the Test Editor entry (#371, schema-locked JSON editor for
    // catalog test definitions) was removed from this nav-item list per
    // bugs-board #197. The route, page component, and its supporting code
    // are unchanged and still reachable directly at /test-defs.
    { icon: <DescriptionIcon />, text: t("nav.reports"), onClick: () => navigate("/reports") },
    {
      icon: <PublishIcon />,
      text: t("nav.publishResults"),
      onClick: () => navigate("/publish-results"),
    },
    {
      // #1026 — read-only show Settings (USEF show id, manager/secretary,
      // core metadata). LIVE-gated controls (FVD-sync switch, re-seed) land
      // on this same page in #1029 — see pages/Settings.jsx.
      icon: <SettingsIcon />,
      text: t("nav.settings"),
      onClick: () => navigate("/settings"),
    },
    {
      // card #2566 — org-level sponsor library (list/create/edit/delete),
      // same score-api entity/routes score-fe's remote-control SponsorsPage
      // already manages (card #1639). Org-scoped like Settings above, not
      // show-scoped, so it stays reachable regardless of the active show.
      icon: <HandshakeIcon />,
      text: t("nav.sponsors"),
      onClick: () => navigate("/sponsors"),
    },
  ];

  // #2373 — appended, not spliced in, so it always renders last regardless
  // of where a future item lands above; absent entirely (not just hidden)
  // for a non-super user, matching the account-fe SuperAwareNavRail pattern.
  if (isSuper) {
    navItems.push({
      icon: <DnsIcon />,
      text: t("nav.superuserFoxbox"),
      onClick: () => navigate("/superuser/foxbox"),
    });
  }

  const accountMenuItems = [
    {
      icon: <SettingsIcon />,
      text: t("accountMenu.accountSettings"),
      onClick: () => {
        window.location.href = import.meta.env.VITE_ACCOUNT_FE_BASE_URL + "/settings";
      },
    },
    {
      icon: mode === "dark" ? <Brightness7Icon /> : <Brightness4Icon />,
      text: mode === "dark" ? t("accountMenu.lightMode") : t("accountMenu.darkMode"),
      onClick: handleThemeToggle,
    },
    {
      icon: <LogoutIcon />,
      text: t("accountMenu.signOut"),
      onClick: handleLogout,
    },
  ];

  const appBarRightContent = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <OrgSwitcher />
      <ShowSwitcher />
      <UserDisplayName />
      <AccountMenu menuItems={accountMenuItems} />
    </Box>
  );

  return (
    <AppLayout
      appBar={
        <AppBar
          sx={{ backgroundColor: colors.darkGrey, color: "#fff" }}
          rightContent={appBarRightContent}
          brandLogo={<BrandLogoMark />}
        />
      }
      navRail={
        <NavRail navItems={navItems} backgroundColor={colors.darkGrey} color="#fff" />
      }
      appFooterEnabled={true}
      appFooter={
        <AppFooter sx={{ backgroundColor: colors.darkGrey, color: "#fff" }}>
          <Typography variant="body2">
            © {new Date().getFullYear()} Fox Village.
          </Typography>
        </AppFooter>
      }
      brandLogo={<BrandLogoMark />}
      appBarRightContent={appBarRightContent}
    >
      {children}
    </AppLayout>
  );
}

function AppContent({ cognitoAuthConfig }) {
  const navigate = useNavigate();
  const location = useLocation();
  useColorScheme();

  return (
    <CognitoAuthProvider cognitoAuthConfig={cognitoAuthConfig} location={location}>
      <ScoreadminActiveOrgProvider>
        <Routes>
          <Route
            path="/callback"
            element={<CallbackPage navigateFunction={navigate} location={location} />}
          />
          <Route path="/logout" element={<LogoutPage navigateFunction={navigate} />} />
          <Route path="/logged-out" element={<LoggedOutPage />} />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                {/* #2120 — ActiveShowProvider is hoisted ABOVE the entitlement
                    gate on purpose: it starts the show-list fetch the moment
                    the route is protected-authenticated, concurrently with
                    ActiveOrgProvider's org-list fetch above, instead of
                    waiting for ScoreEntitlementGate to resolve first. The gate
                    itself still fully blocks AdminShell (the actual admin
                    content) until it decides — only the *data fetch* start
                    time moved, not the access decision. */}
                <ScoreadminActiveShowProvider>
                  <ScoreEntitlementGate>
                    <AdminShell>
                      <Routes>
                        <Route path="/" element={<ShowDashboard />} />
                      <Route path="/classes" element={<ClassManager />} />
                      {/* #863 — deep-linkable class detail; ClassManager reads
                          :classId via useParams to drive the ClassDetailDrawer
                          (mirrors /rides/:rideId below). */}
                      <Route path="/classes/:classId" element={<ClassManager />} />
                      <Route path="/horses" element={<HorseManager />} />
                      <Route path="/people" element={<PeopleManager />} />
                      <Route path="/judges" element={<JudgeManager />} />
                      <Route path="/reports" element={<ReportsPage />} />
                      <Route path="/publish-results" element={<PublishResults />} />
                      {/* #508 — no rideId shows the show/class/ride picker;
                          #507's RideHistoryDrawer already links straight to
                          /rides/:rideId. */}
                      <Route path="/rides" element={<RideEditor />} />
                      <Route path="/rides/:rideId" element={<RideEditor />} />
                      {/* #837 — the re-point tool; kept off /rides on purpose,
                          see the comment atop RidePointer.jsx. */}
                      <Route path="/ride-pointer" element={<RidePointer />} />
                      <Route path="/placings" element={<ClassPlacings />} />
                      <Route path="/review-queue" element={<ReviewQueue />} />
                      <Route path="/test-defs" element={<TestDefEditor />} />
                      <Route path="/settings" element={<SettingsPage />} />
                      <Route path="/sponsors" element={<Sponsors />} />
                      {/* #2373 — instance-level, superuser-only. Deliberately
                          OUTSIDE any show-scoped route grouping: it must render
                          with no show selected and regardless of which show is
                          active (card AC2). FoxboxInstances itself redirects a
                          non-super caller (AC5) — ScoreEntitlementGate above
                          only enforces org score-entitlement + role, a lower
                          bar than isSuper. */}
                      <Route path="/superuser/foxbox" element={<FoxboxInstances />} />
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                    </AdminShell>
                  </ScoreEntitlementGate>
                </ScoreadminActiveShowProvider>
              </ProtectedRoute>
            }
          />
        </Routes>
      </ScoreadminActiveOrgProvider>
    </CognitoAuthProvider>
  );
}

export default function App({ cognitoAuthConfig }) {
  const theme = createBrandTheme();
  return (
    <ThemeProvider theme={theme} defaultMode="light">
      <CssBaseline />
      <AppContent cognitoAuthConfig={cognitoAuthConfig} />
      <UsaBadge />
      <BuildBadge />
    </ThemeProvider>
  );
}
